/**
 * 图片压缩工具
 * 用于客户端上传前压缩图片，提升上传速度
 */

/**
 * 压缩图片
 * @param {File} file - 原始图片文件
 * @param {Object} options - 压缩选项
 * @param {number} options.maxSize - 最大尺寸（宽或高），默认 1920
 * @param {number} options.quality - 压缩质量 0-1，默认 0.8
 * @returns {Promise<File>} 压缩后的图片文件
 */
export async function compressImage(file, options = {}) {
  const { maxSize = 1920, quality = 0.8 } = options

  // GIF 不压缩，保留动画
  if (file.type === 'image/gif') {
    return file
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      const img = new Image()

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          let width = img.width
          let height = img.height

          // 如果图片尺寸过大，进行缩放
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = (height / width) * maxSize
              width = maxSize
            } else {
              width = (width / height) * maxSize
              height = maxSize
            }
          }

          canvas.width = width
          canvas.height = height

          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)

          // 转换为 Blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // 创建新的 File 对象
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                })
                resolve(compressedFile)
              } else {
                reject(new Error('图片压缩失败'))
              }
            },
            'image/jpeg',
            quality
          )
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => {
        reject(new Error('图片加载失败'))
      }

      img.src = e.target.result
    }

    reader.onerror = () => {
      reject(new Error('文件读取失败'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * 快速压缩（适用于客户端，更激进的压缩）
 * @param {File} file - 原始图片文件
 * @returns {Promise<File>} 压缩后的图片文件
 */
export async function compressImageFast(file) {
  return compressImage(file, {
    maxSize: 1280,  // 更小的尺寸
    quality: 0.7    // 更低的质量
  })
}

/**
 * 标准压缩（适用于客服端，平衡质量和大小）
 * @param {File} file - 原始图片文件
 * @returns {Promise<File>} 压缩后的图片文件
 */
export async function compressImageStandard(file) {
  return compressImage(file, {
    maxSize: 1920,
    quality: 0.8
  })
}
